import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";
import { createJob, updateJob, createCustomer, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const EMPTY = {
    customer: "", customer_id: "", task: "", priority: "Medium", employee: "", employees: [],
    deadline: "", status: "Ny", description: "", address: "", time_hours: 0,
    customer_message: "", ai_response: "",
    // n8n-passthrough (read-only in UI)
    time_calculated_text: "", assigned_employee_name: "", employee_signoff_status: "",
};

const PRIORITIES = ["Høj", "Medium", "Lav"];
const STATUSES = ["Ny", "Aktiv", "I gang", "Færdig", "Afsluttet"];

export default function JobFormDialog({ open, job, customers = [], onClose, onSaved }) {
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const [employeesText, setEmployeesText] = useState("");
    const [newCustomer, setNewCustomer] = useState(false);
    const [newPhone, setNewPhone] = useState("");
    const isEdit = !!job?.id;

    useEffect(() => {
        if (!open) return;
        const next = job ? { ...EMPTY, ...job } : EMPTY;
        // Normalize employees: prefer array, fallback to single employee string
        const list = Array.isArray(next.employees) && next.employees.length > 0
            ? next.employees
            : (next.employee ? [next.employee] : []);
        next.employees = list;
        setForm(next);
        setEmployeesText(list.join(", "));
        setNewCustomer(false);
        setNewPhone("");
    }, [open, job]);

    const upd = (k, isNum) => (e) => setForm((f) => ({ ...f, [k]: isNum ? parseFloat(e.target.value || 0) : e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            // Inline new-customer creation (spec 5B) — create then link.
            let customerId = form.customer_id || "";
            if (!isEdit && newCustomer && form.customer.trim()) {
                const created = await createCustomer({
                    name: form.customer.trim(), phone: newPhone || "", address: form.address || "", force: true,
                });
                if (created && created.id) customerId = created.id;
            }
            // Parse comma-separated employees → array (trim, dedupe, drop blanks)
            const employeesArr = Array.from(new Set(
                employeesText.split(",").map((s) => s.trim()).filter(Boolean)
            ));
            const payload = {
                customer: form.customer,
                customer_id: customerId,
                task: form.task,
                priority: form.priority,
                employee: employeesArr[0] || form.employee || "",
                employees: employeesArr,
                deadline: form.deadline || "",
                status: form.status || "Aktiv",
                description: form.description || "",
                address: form.address || "",
                time_hours: Number(form.time_hours) || 0,
                customer_message: form.customer_message || "",
                ai_response: form.ai_response || "",
                // Preserve n8n fields so PUT round-trips them
                time_calculated_text: form.time_calculated_text || "",
                assigned_employee_name: form.assigned_employee_name || "",
                employee_signoff_status: form.employee_signoff_status || "",
            };
            const saved = isEdit ? await updateJob(job.id, payload) : await createJob(payload);
            toast.success(isEdit ? "Job opdateret" : "Job oprettet");
            onSaved(saved);
            onClose();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const pickCustomer = (cid) => {
        const c = customers.find((x) => x.id === cid);
        setForm((f) => ({ ...f, customer_id: cid, customer: c ? c.name : f.customer, address: c?.address || f.address }));
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle>{isEdit ? "Redigér job" : "Opret job"}</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                    <div>
                        <Label>Kunde (fra liste)</Label>
                        <select
                            data-testid="job-form-customer-select"
                            value={form.customer_id || ""}
                            onChange={(e) => pickCustomer(e.target.value)}
                            className="w-full h-10 mt-1 rounded-md border border-slate-200 bg-white px-2 text-sm"
                        >
                            <option value="">— Vælg kunde eller skriv navn —</option>
                            {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    </div>
                    <div>
                        <Label>Kundenavn</Label>
                        <Input required data-testid="job-form-customer" value={form.customer} onChange={upd("customer")} className="h-10 mt-1" />
                    </div>
                    <div className="sm:col-span-2 -mt-1">
                        <label className="inline-flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                            <input type="checkbox" data-testid="job-form-newcustomer-toggle" checked={newCustomer}
                                onChange={(e) => setNewCustomer(e.target.checked)} className="rounded border-slate-300" />
                            Opret som ny kunde (gemmes i Kunder)
                        </label>
                        {newCustomer && (
                            <Input data-testid="job-form-newcustomer-phone" value={newPhone} onChange={(e) => setNewPhone(e.target.value)}
                                placeholder="Telefon (valgfri)" className="h-10 mt-2" />
                        )}
                    </div>
                    <div className="sm:col-span-2">
                        <Label>Opgave</Label>
                        <Input required data-testid="job-form-task" value={form.task} onChange={upd("task")} placeholder="Fx Servicebesøg" className="h-10 mt-1" />
                    </div>
                    <div>
                        <Label>Prioritet</Label>
                        <div className="flex gap-1.5 mt-1">
                            {PRIORITIES.map((p) => (
                                <button key={p} type="button" data-testid={`job-form-prio-${p}`} onClick={() => setForm((f) => ({ ...f, priority: p }))}
                                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border ${form.priority === p ? "bg-blue-50 border-blue-300 text-blue-700" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{p}</button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <Label>Status</Label>
                        <select data-testid="job-form-status" value={form.status} onChange={upd("status")} className="w-full h-10 mt-1 rounded-md border border-slate-200 bg-white px-2 text-sm">
                            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </div>
                    <div className="sm:col-span-2">
                        <Label>Medarbejdere på opgaven</Label>
                        <Input
                            data-testid="job-form-employees"
                            value={employeesText}
                            onChange={(e) => setEmployeesText(e.target.value)}
                            placeholder="Adskil flere med komma — fx Peter, Maria, Jonas"
                            className="h-10 mt-1"
                        />
                        <p className="text-[10px] text-slate-400 mt-1">Skriv én eller flere medarbejdere adskilt med komma.</p>
                    </div>
                    <div>
                        <Label>Deadline</Label>
                        <Input data-testid="job-form-deadline" value={form.deadline} onChange={upd("deadline")} placeholder="I dag / Fredag" className="h-10 mt-1" />
                    </div>
                    <div>
                        <Label>Adresse</Label>
                        <Input data-testid="job-form-address" value={form.address} onChange={upd("address")} className="h-10 mt-1" />
                    </div>
                    <div>
                        <Label>Tid (timer)</Label>
                        <Input type="number" step="0.25" data-testid="job-form-hours" value={form.time_hours} onChange={upd("time_hours", true)} className="h-10 mt-1" />
                    </div>
                    <div className="sm:col-span-2">
                        <Label>Opgavebeskrivelse</Label>
                        <Textarea data-testid="job-form-desc" value={form.description} onChange={upd("description")} className="min-h-[80px] mt-1" />
                    </div>
                    <div className="sm:col-span-2">
                        <Label>Kundebesked</Label>
                        <Textarea data-testid="job-form-msg" value={form.customer_message} onChange={upd("customer_message")} className="min-h-[60px] mt-1" />
                    </div>
                    {/* Integration-driven read-only fields (auto-populated by ElevenLabs / Voice Intake) */}
                    {(form.time_calculated_text || form.assigned_employee_name || form.employee_signoff_status) && (() => {
                        // Normalize integration employee data → array of names. Supports array OR comma/semicolon/newline separated string.
                        const toNames = (val) => {
                            if (!val) return [];
                            if (Array.isArray(val)) return val.map((s) => String(s).trim()).filter(Boolean);
                            return String(val).split(/[,;\n]/).map((s) => s.trim()).filter(Boolean);
                        };
                        const tilmeldt = toNames(form.assigned_employee_name);
                        const frameldt = toNames(form.employee_signoff_status);
                        return (
                            <div className="sm:col-span-2 rounded-xl border border-blue-100 bg-blue-50/40 p-3 space-y-3" data-testid="job-form-integration-fields">
                                <p className="text-[10px] uppercase tracking-wider text-blue-700 font-bold">Fra integration (kan ikke redigeres)</p>

                                {form.time_calculated_text && (
                                    <div>
                                        <p className="text-[10px] text-slate-500">Beregnet tid</p>
                                        <p className="font-semibold text-slate-900 font-mono text-xs" data-testid="job-form-time-calculated">{form.time_calculated_text}</p>
                                    </div>
                                )}

                                <div data-testid="job-form-employee-status">
                                    <p className="text-xs font-bold text-slate-800 mb-2">Medarbejdere på opgaven</p>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                        <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-2">
                                            <p className="text-[11px] font-semibold text-emerald-800" data-testid="job-form-tilmeldt-header">
                                                Tilmeldt ({tilmeldt.length})
                                            </p>
                                            {tilmeldt.length === 0 ? (
                                                <p className="text-xs text-slate-500 italic mt-1" data-testid="job-form-tilmeldt-empty">Ingen</p>
                                            ) : (
                                                <ul className="mt-1 space-y-0.5" data-testid="job-form-tilmeldt-list">
                                                    {tilmeldt.map((n, i) => (
                                                        <li key={`t-${i}`} className="text-xs text-slate-900 font-medium">• {n}</li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                        <div className="rounded-lg border border-rose-200 bg-rose-50/60 p-2">
                                            <p className="text-[11px] font-semibold text-rose-800" data-testid="job-form-frameldt-header">
                                                Frameldt ({frameldt.length})
                                            </p>
                                            {frameldt.length === 0 ? (
                                                <p className="text-xs text-slate-500 italic mt-1" data-testid="job-form-frameldt-empty">Ingen</p>
                                            ) : (
                                                <ul className="mt-1 space-y-0.5" data-testid="job-form-frameldt-list">
                                                    {frameldt.map((n, i) => (
                                                        <li key={`f-${i}`} className="text-xs text-slate-900 font-medium">• {n}</li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })()}
                    <div className="sm:col-span-2 flex justify-end gap-2 mt-2">
                        <Button type="button" variant="outline" onClick={onClose}>Annullér</Button>
                        <Button type="submit" disabled={saving} data-testid="job-form-submit" className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
                            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                            {isEdit ? "Gem ændringer" : "Opret job"}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}
